import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMatch } from '../context/MatchContext';
import { calculateAthleteStats, calculateRoundStats, calculateCrossUnitPatterns, calculateTaggedSequences } from '../utils/statistics';
import { getTechniqueById } from '../utils/atcTechniques';
import { getFlagByAlpha3 } from '../utils/tkdCountries';
import { getActions, getPenalties, getSession } from '../services/api';

const ACTION_TYPE_LABELS = {
  a: 'Attacco', d: 'Difesa', c: 'Contro', ca: 'Ca', ci: 'Ci', c2: 'C2',
};

function techName(id, custom) {
  if (id === 'custom') return custom || 'Custom';
  return getTechniqueById(id)?.name || id;
}

function StatRow({ label, blueVal, redVal, blueClass, redClass }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 1fr', gap: 4, alignItems: 'center', padding: '3px 0', borderBottom: '1px solid var(--color-border)' }}>
      <span style={{ textAlign: 'right', color: blueClass === 'blue' ? 'var(--color-blue)' : 'var(--color-text-sub)', fontWeight: blueClass ? 600 : 400 }}>{blueVal}</span>
      <span style={{ textAlign: 'center', fontSize: 10, color: 'var(--color-text-dim)' }}>{label}</span>
      <span style={{ textAlign: 'left', color: redClass === 'red' ? 'var(--color-red)' : 'var(--color-text-sub)', fontWeight: redClass ? 600 : 400 }}>{redVal}</span>
    </div>
  );
}

function TechTable({ title, entries, color }) {
  if (!entries.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginBottom: 4 }}>{title}</div>
      {entries.map(([tech, count, pts]) => (
        <div key={tech} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0', borderBottom: '1px solid var(--color-border)' }}>
          <span style={{ color: 'var(--color-text-sub)' }}>{tech}</span>
          <span style={{ color: 'var(--color-text-dim)' }}>{count}x {pts > 0 && <span style={{ color }}>+{pts}pt</span>}</span>
        </div>
      ))}
    </div>
  );
}

/** Converte una stringa "Bn→Dl" in "Bandal chagi → Dollyo chagi" */
function resolveSeqLabel(seq) {
  return seq.split('→').map(id => getTechniqueById(id)?.name || id).join(' → ');
}

function SequenceSection({ sequences, color, limit = 8, raw = false }) {
  if (!sequences.length) return (
    <div style={{ fontSize: 11, color: 'var(--color-text-dim)', textAlign: 'center', padding: '8px 0' }}>
      Dati insufficienti
    </div>
  );
  const top = sequences.slice(0, limit);
  const maxCount = top[0].count;
  return (
    <div>
      {top.map(({ seq, count, scored, points }) => {
        const pct = Math.round((count / maxCount) * 100);
        const scorePct = count > 0 ? Math.round((scored / count) * 100) : 0;
        return (
          <div key={seq} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: 'var(--color-text-sub)' }}>{raw ? seq : resolveSeqLabel(seq)}</span>
              <span style={{ fontSize: 10, color: 'var(--color-text-dim)', whiteSpace: 'nowrap', marginLeft: 8 }}>
                {count}x
                {scored > 0 && <span style={{ color, marginLeft: 4 }}>· {scored} punto{scored > 1 ? 'i' : ''} ({scorePct}%)</span>}
                {points > 0 && <span style={{ color, marginLeft: 4 }}>+{points}pt</span>}
              </span>
            </div>
            <div style={{ height: 3, background: 'var(--bg-elevated)', borderRadius: 2 }}>
              <div style={{ height: 3, width: `${pct}%`, background: color, borderRadius: 2, opacity: scored > 0 ? 1 : 0.35 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const SEQ_PALETTE = ['#9C27B0', 'var(--color-orange)', '#00BCD4', '#8BC34A', '#E91E63', 'var(--color-orange)'];

function TaggedSeqSection({ sequences, color }) {
  if (!sequences.length) return (
    <div style={{ fontSize: 11, color: 'var(--color-text-dim)', textAlign: 'center', padding: '8px 0' }}>
      Nessuna sequenza taggata
    </div>
  );
  return (
    <div>
      {sequences.map(({ seqId, acts, points, scored }) => {
        const label = acts.map(a => techName(a.technique, a.custom_technique)).join(' → ');
        const ts    = acts[0].timestamp_display || '--:--.-';
        const types = acts.map(a => ACTION_TYPE_LABELS[a.action_type] || a.action_type).join(' → ');
        return (
          <div key={seqId} style={{ marginBottom: 8, padding: '6px 8px', background: 'var(--bg-secondary)', borderRadius: 4, borderLeft: `3px solid ${color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
              <span style={{ fontSize: 11, color: 'var(--color-text-sub)' }}>{label}</span>
              <span style={{ fontSize: 10, color: 'var(--color-text-dim)', marginLeft: 8, whiteSpace: 'nowrap' }}>{ts}</span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>
              {types} · {acts.length} azioni
              {scored > 0 && <span style={{ color, marginLeft: 8 }}>· +{points}pt</span>}
              {scored === 0 && <span style={{ color: 'var(--color-text-dim)', marginLeft: 8 }}>· nessun punto</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReportPage() {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const { state }  = useMatch();

  // Dati remoti (sessioni storiche non in localStorage)
  const [remoteData, setRemoteData] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  // Se l'ID nell'URL non corrisponde alla sessione in localStorage, carica da API
  const localMatchesUrl = state.isSetupComplete && state.session &&
    (String(state.session.id) === String(id) || !id || id === 'local');

  useEffect(() => {
    document.body.classList.add('page-scrollable');
    return () => document.body.classList.remove('page-scrollable');
  }, []);

  useEffect(() => {
    if (localMatchesUrl) return;
    if (!id || id === 'local') return;
    setLoading(true);
    Promise.all([
      getSession(id),
      getActions(id),
      getPenalties(id),
    ])
      .then(([session, actions, penalties]) => setRemoteData({ session, actions, penalties }))
      .catch(() => setError('Impossibile caricare il report da server.'))
      .finally(() => setLoading(false));
  }, [id, localMatchesUrl]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-secondary)', color: 'var(--color-text-sub)', fontSize: 14 }}>
        Caricamento report…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-secondary)', color: '#fff', gap: 16 }}>
        <div style={{ color: 'var(--color-red)' }}>{error}</div>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          ← Dashboard
        </button>
      </div>
    );
  }

  // Usa dati remoti se disponibili, altrimenti localStorage
  const session   = remoteData ? remoteData.session   : state.session;
  const actions   = remoteData ? remoteData.actions   : state.actions;
  const penalties = remoteData ? remoteData.penalties : state.penalties;
  const scores    = state.scores; // verrà ricalcolato sotto per i dati remoti

  if (!session) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-secondary)', color: '#fff', gap: 16 }}>
        <div style={{ fontSize: 20, color: 'var(--color-text-dim)' }}>Nessuna sessione trovata</div>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          ← Dashboard
        </button>
      </div>
    );
  }

  // Ricalcola score dai dati effettivi (necessario per sessioni remote)
  const computedBlueScore = actions.filter(a => a.athlete === 'blue' && a.outcome === 'punto').reduce((s, a) => s + (a.points || 0), 0)
    + penalties.filter(p => p.athlete === 'red').length;
  const computedRedScore  = actions.filter(a => a.athlete === 'red'  && a.outcome === 'punto').reduce((s, a) => s + (a.points || 0), 0)
    + penalties.filter(p => p.athlete === 'blue').length;
  const effectiveScores = remoteData
    ? { blue: computedBlueScore, red: computedRedScore }
    : (scores || { blue: computedBlueScore, red: computedRedScore });

  const blueStats = calculateAthleteStats(actions, 'blue');
  const redStats  = calculateAthleteStats(actions, 'red');
  const roundStats = calculateRoundStats(actions, penalties, session.num_rounds || 3);

  const bluePairs    = calculateCrossUnitPatterns(actions, 'blue', 2, techName);
  const redPairs     = calculateCrossUnitPatterns(actions, 'red',  2, techName);
  const blueTriples  = calculateCrossUnitPatterns(actions, 'blue', 3, techName);
  const redTriples   = calculateCrossUnitPatterns(actions, 'red',  3, techName);

  const blueTaggedSeqs = calculateTaggedSequences(actions, 'blue');
  const redTaggedSeqs  = calculateTaggedSequences(actions, 'red');

  const blueName = session.athlete_blue_name  || 'BLU';
  const redName  = session.athlete_red_name   || 'ROSSO';
  const blueFlag = getFlagByAlpha3(session.athlete_blue_nationality);
  const redFlag  = getFlagByAlpha3(session.athlete_red_nationality);

  // Tecniche usate con punti segnati
  function techBreakdown(athlete) {
    const map = {};
    actions.filter(a => a.athlete === athlete && a.outcome === 'punto').forEach(a => {
      const key = techName(a.technique, a.custom_technique);
      if (!map[key]) map[key] = { count: 0, pts: 0 };
      map[key].count += 1;
      map[key].pts   += a.points || 0;
    });
    return Object.entries(map).map(([k, v]) => [k, v.count, v.pts]).sort((a, b) => b[1] - a[1]);
  }

  // Tecniche subite con punti concessi
  function techReceivedBreakdown(athlete) {
    const opp = athlete === 'blue' ? 'red' : 'blue';
    const map = {};
    actions.filter(a => a.athlete === opp && a.outcome === 'punto').forEach(a => {
      const key = techName(a.technique, a.custom_technique);
      if (!map[key]) map[key] = { count: 0, pts: 0 };
      map[key].count += 1;
      map[key].pts   += a.points || 0;
    });
    return Object.entries(map).map(([k, v]) => [k, v.count, v.pts]).sort((a, b) => b[1] - a[1]);
  }

  // Timeline
  const allEvents = [
    ...actions.map(a => ({ ...a, _type: 'action' })),
    ...penalties.map(p => ({ ...p, _type: 'penalty' })),
  ].sort((a, b) => (a.timestamp_video || 0) - (b.timestamp_video || 0));

  // Colori e posizioni per le sequenze taggate nella timeline
  const seqColorMap = {};
  let _seqPaletteIdx = 0;
  const seqPositions = new Array(allEvents.length).fill(null);
  const seqPosCount  = {};
  allEvents.forEach((ev, i) => {
    if (!ev.sequence_id) return;
    if (!seqColorMap[ev.sequence_id]) {
      seqColorMap[ev.sequence_id] = SEQ_PALETTE[_seqPaletteIdx++ % SEQ_PALETTE.length];
    }
    seqPosCount[ev.sequence_id] = (seqPosCount[ev.sequence_id] || 0) + 1;
    seqPositions[i] = seqPosCount[ev.sequence_id];
  });

  const winner = effectiveScores.blue > effectiveScores.red ? 'blue' : effectiveScores.red > effectiveScores.blue ? 'red' : null;

  return (
    <div style={{ background: 'var(--bg-secondary)', minHeight: '100vh', color: 'var(--color-text-sub)', fontFamily: 'monospace', padding: '20px 16px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')}>
          ← Dashboard
        </button>
        <div style={{ textAlign: 'center' }}>
          {session.event_name && <div style={{ fontSize: 13, color: 'var(--color-text-dim)' }}>{session.event_name}</div>}
          {session.weight_category && <div style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>{session.weight_category}</div>}
          {session.event_date && <div style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>{session.event_date}</div>}
        </div>
        <div style={{ width: 60 }} />
      </div>

      {/* Scoreboard finale */}
      <div className="rp-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, marginBottom: 24, padding: 20 }}>
        <div style={{ textAlign: 'center', flex: 1 }}>
          {blueFlag && <div style={{ fontSize: 24 }}>{blueFlag}</div>}
          <div style={{ fontSize: 14, color: 'var(--color-blue)', fontWeight: 700 }}>{blueName}</div>
          {session.athlete_blue_nationality && <div style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>{session.athlete_blue_nationality}</div>}
          {winner === 'blue' && <div style={{ fontSize: 10, color: 'var(--color-green)', marginTop: 4 }}>VINCITORE</div>}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <span style={{ fontSize: 48, fontWeight: 900, color: effectiveScores.blue > effectiveScores.red ? 'var(--color-blue)' : 'var(--color-text-sub)' }}>{effectiveScores.blue}</span>
            <span style={{ fontSize: 24, color: 'var(--color-text-dim)' }}>:</span>
            <span style={{ fontSize: 48, fontWeight: 900, color: effectiveScores.red > effectiveScores.blue ? 'var(--color-red)' : 'var(--color-text-sub)' }}>{effectiveScores.red}</span>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 8 }}>
            {roundStats.map(r => (
              <span key={r.round} style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>
                R{r.round}: <span style={{ color: 'var(--color-blue)' }}>{r.blueScore}</span>-<span style={{ color: 'var(--color-red)' }}>{r.redScore}</span>
              </span>
            ))}
          </div>
          {penalties.length > 0 && (
            <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginTop: 4 }}>
              Gam-jeom BLU: {penalties.filter(p => p.athlete === 'blue').length} &nbsp;|&nbsp; ROSSO: {penalties.filter(p => p.athlete === 'red').length}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          {redFlag && <div style={{ fontSize: 24 }}>{redFlag}</div>}
          <div style={{ fontSize: 14, color: 'var(--color-red)', fontWeight: 700 }}>{redName}</div>
          {session.athlete_red_nationality && <div style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>{session.athlete_red_nationality}</div>}
          {winner === 'red' && <div style={{ fontSize: 10, color: 'var(--color-green)', marginTop: 4 }}>VINCITORE</div>}
        </div>
      </div>

      {/* Stats comparative */}
      <div className="rp-panel">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 1fr', marginBottom: 8 }}>
          <span style={{ textAlign: 'right', color: 'var(--color-blue)', fontWeight: 700, fontSize: 12 }}>{blueName}</span>
          <span />
          <span style={{ textAlign: 'left', color: 'var(--color-red)', fontWeight: 700, fontSize: 12 }}>{redName}</span>
        </div>
        <StatRow label="Azioni totali"     blueVal={blueStats.totalActions}      redVal={redStats.totalActions} />
        <StatRow label="Attacchi"          blueVal={blueStats.totalAttacks}      redVal={redStats.totalAttacks} />
        <StatRow label="Difese"            blueVal={blueStats.totalDefenses}     redVal={redStats.totalDefenses} />
        <StatRow label="Contrattacchi"     blueVal={blueStats.totalCounters}     redVal={redStats.totalCounters} />
        <StatRow label="Punti segnati"     blueVal={blueStats.totalPoints}       redVal={redStats.totalPoints}
                                           blueClass={effectiveScores.blue > effectiveScores.red ? 'blue' : null}
                                           redClass={effectiveScores.red > effectiveScores.blue ? 'red' : null} />
        <StatRow label="Punti subiti"      blueVal={blueStats.pointsConceded}    redVal={redStats.pointsConceded} />
        <StatRow label="Eff. attacco"      blueVal={`${blueStats.attackEfficiency}%`}  redVal={`${redStats.attackEfficiency}%`} />
        <StatRow label="Res. difesa"       blueVal={`${blueStats.defenseEfficiency}%`} redVal={`${redStats.defenseEfficiency}%`} />
        <StatRow label="Success rate"      blueVal={`${blueStats.successRate}%`}  redVal={`${redStats.successRate}%`} />
      </div>

      {/* Tecniche */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div className="rp-panel" style={{ marginBottom: 0 }}>
          <div style={{ color: 'var(--color-blue)', fontWeight: 700, fontSize: 12, marginBottom: 8 }}>{blueName}</div>
          <TechTable title="Tecniche usate (con punto)" entries={techBreakdown('blue')} color="var(--color-blue)" />
          <TechTable title="Tecniche subite" entries={techReceivedBreakdown('blue')} color="var(--color-red)" />
        </div>
        <div className="rp-panel" style={{ marginBottom: 0 }}>
          <div style={{ color: 'var(--color-red)', fontWeight: 700, fontSize: 12, marginBottom: 8 }}>{redName}</div>
          <TechTable title="Tecniche usate (con punto)" entries={techBreakdown('red')} color="var(--color-red)" />
          <TechTable title="Tecniche subite" entries={techReceivedBreakdown('red')} color="var(--color-blue)" />
        </div>
      </div>

      {/* Pattern Tattici */}
      <div className="rp-panel">
        <div className="ds-section-label" style={{ marginBottom: 4 }}>PATTERN TATTICI</div>
        <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginBottom: 16 }}>
          Azioni singole e sequenze ⛓ come unità — pattern che si ripetono ≥2 volte
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* BLU */}
          <div>
            <div style={{ color: 'var(--color-blue)', fontWeight: 700, fontSize: 12, marginBottom: 10 }}>{blueName}</div>
            <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginBottom: 6, letterSpacing: 0.6 }}>COPPIE</div>
            <SequenceSection sequences={bluePairs} color="var(--color-blue)" raw />
            {blueTriples.length > 0 && (
              <>
                <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginBottom: 6, marginTop: 14, letterSpacing: 0.6 }}>TRIPLE</div>
                <SequenceSection sequences={blueTriples} color="var(--color-blue)" limit={5} raw />
              </>
            )}
          </div>

          {/* ROSSO */}
          <div>
            <div style={{ color: 'var(--color-red)', fontWeight: 700, fontSize: 12, marginBottom: 10 }}>{redName}</div>
            <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginBottom: 6, letterSpacing: 0.6 }}>COPPIE</div>
            <SequenceSection sequences={redPairs} color="var(--color-red)" raw />
            {redTriples.length > 0 && (
              <>
                <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginBottom: 6, marginTop: 14, letterSpacing: 0.6 }}>TRIPLE</div>
                <SequenceSection sequences={redTriples} color="var(--color-red)" limit={5} raw />
              </>
            )}
          </div>

        </div>
      </div>

      {/* Sequenze Taggate */}
      {(blueTaggedSeqs.length > 0 || redTaggedSeqs.length > 0) && (
        <div className="rp-panel">
          <div className="ds-section-label" style={{ marginBottom: 16 }}>SEQUENZE TAGGATE ⛓</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <div style={{ color: 'var(--color-blue)', fontWeight: 700, fontSize: 12, marginBottom: 10 }}>{blueName}</div>
              <TaggedSeqSection sequences={blueTaggedSeqs} color="var(--color-blue)" />
            </div>
            <div>
              <div style={{ color: 'var(--color-red)', fontWeight: 700, fontSize: 12, marginBottom: 10 }}>{redName}</div>
              <TaggedSeqSection sequences={redTaggedSeqs} color="var(--color-red)" />
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="rp-panel" style={{ marginBottom: 0 }}>
        <div className="ds-section-label" style={{ marginBottom: 12 }}>TIMELINE AZIONI ({allEvents.length})</div>
        {allEvents.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 12 }}>Nessuna azione registrata</div>
        )}
        {allEvents.map((ev, i) => {
          const isBlue   = ev.athlete === 'blue';
          const color    = isBlue ? 'var(--color-blue)' : 'var(--color-red)';
          const name     = isBlue ? blueName : redName;
          const seqColor = ev.sequence_id ? seqColorMap[ev.sequence_id] : null;
          const seqPos   = seqPositions[i];

          if (ev._type === 'penalty') {
            return (
              <div key={`p${ev.id}`} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: '1px solid var(--color-border)', fontSize: 11, alignItems: 'center' }}>
                <span style={{ color: 'var(--color-text-dim)', minWidth: 52 }}>{ev.timestamp_display || '--:--.-'}</span>
                <span style={{ color: 'var(--color-text-dim)', minWidth: 16 }}>R{ev.round_number}</span>
                <span style={{ color, fontWeight: 600, minWidth: 70 }}>{name}</span>
                <span style={{ color: 'var(--color-orange)' }}>Gam-jeom — {ev.penalty_type || ''}</span>
              </div>
            );
          }

          const pts = ev.points || 0;
          const ptsColor = pts >= 6 ? 'var(--color-red)' : pts >= 3 ? 'var(--color-orange)' : pts > 0 ? 'var(--color-green)' : 'var(--color-text-dim)';
          const outcomeStr = ev.outcome === 'punto' ? `+${pts}` : ev.outcome === 'tocco' ? '~' : 'X';

          return (
            <div
              key={`a${ev.id}`}
              style={{
                display: 'flex', gap: 8, padding: '4px 0', borderBottom: '1px solid var(--color-border)',
                fontSize: 11, alignItems: 'center',
                ...(seqColor ? {
                  borderLeft: `3px solid ${seqColor}`,
                  paddingLeft: 5,
                  background: `${seqColor}18`,
                } : {}),
              }}
            >
              {seqColor
                ? <span style={{ color: seqColor, fontSize: 9, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>⛓{seqPos}</span>
                : <span style={{ minWidth: 20 }} />
              }
              <span style={{ color: 'var(--color-text-dim)', minWidth: 52 }}>{ev.timestamp_display || '--:--.-'}</span>
              <span style={{ color: 'var(--color-text-dim)', minWidth: 16 }}>R{ev.round_number}</span>
              <span style={{ color, fontWeight: 600, minWidth: 70 }}>{name}</span>
              <span style={{ color: 'var(--color-text-dim)', minWidth: 30 }}>{ACTION_TYPE_LABELS[ev.action_type] || ev.action_type}</span>
              <span style={{ color: 'var(--color-text-sub)', flex: 1 }}>
                {techName(ev.technique, ev.custom_technique)}
                {ev.target && ` · ${ev.target}`}
                {ev.technical_bonus ? ' · rot.' : ''}
              </span>
              <span style={{ color: ptsColor, fontWeight: 700, minWidth: 24, textAlign: 'right' }}>{outcomeStr}</span>
            </div>
          );
        })}
      </div>

    </div>
  );
}

export default ReportPage;
